/**
 * Bottom-sheet chooser for compliance / onboarding uploads.
 *
 * Renders Camera · Photo library · PDF over a translucent overlay. The parent
 * owns the open/close state and dispatches the chosen source via `onPick`; this
 * component only renders.
 *
 * `sources` narrows the list for callers that cannot accept every kind — the
 * medications hub offers camera + library only, because the extraction worker
 * reads bottle photos and has no PDF path. Defaults to all three, so existing
 * callers are unaffected.
 *
 * Identity Verification does NOT use this sheet; that flow stays image-only.
 */
import { useCallback } from "react";
import { Modal, Pressable, StyleSheet, TouchableOpacity, View } from "react-native";
import { Icon, Text } from "react-native-paper";

import { colors, radius, spacing, typography } from "@/lib/theme";

export type UploadSource = "camera" | "photo" | "pdf";

interface UploadSourceSheetProps {
    visible: boolean;
    onClose: () => void;
    onPick: (source: UploadSource) => void;
    /** Optional label above the buttons — e.g. "Upload new TB test". */
    title?: string;
    /** Which sources to offer, in order. Defaults to camera + photo + pdf. */
    sources?: readonly UploadSource[];
    /** Overrides the secondary line under a source, keyed by source. */
    subtitles?: Partial<Record<UploadSource, string>>;
}

const ALL_SOURCES: readonly UploadSource[] = ["camera", "photo", "pdf"];

const SOURCE_META: Record<UploadSource, { icon: string; label: string; sub: string }> = {
    camera: { icon: "camera", label: "Take a photo", sub: "Use your camera" },
    photo: {
        icon: "image-multiple-outline",
        label: "Choose from photos",
        sub: "Pick an image from your library"
    },
    pdf: { icon: "file-pdf-box", label: "Upload a PDF", sub: "Select a PDF from Files" }
};

export function UploadSourceSheet({
    visible,
    onClose,
    onPick,
    title,
    sources = ALL_SOURCES,
    subtitles
}: UploadSourceSheetProps) {
    // Wraps onPick so callers don't have to remember to close the sheet —
    // every source pick closes the modal before firing the picker.
    const handlePick = useCallback(
        (source: UploadSource) => {
            onClose();
            // Defer one tick so the modal animates out before the system
            // picker animates in — otherwise iOS sometimes drops the picker
            // presentation.
            setTimeout(() => onPick(source), 50);
        },
        [onClose, onPick]
    );

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
            {/* Tapping the dimmed area closes the sheet — standard mobile pattern. */}
            <Pressable style={styles.overlay} onPress={onClose}>
                <Pressable style={styles.sheet} onPress={(e) => e.stopPropagation()}>
                    {title ? <Text style={styles.title}>{title}</Text> : null}
                    {sources.map((source) => (
                        <SourceButton
                            key={source}
                            icon={SOURCE_META[source].icon}
                            label={SOURCE_META[source].label}
                            sub={subtitles?.[source] ?? SOURCE_META[source].sub}
                            onPress={() => handlePick(source)}
                        />
                    ))}
                    <TouchableOpacity style={styles.cancel} onPress={onClose}>
                        <Text style={styles.cancelText}>Cancel</Text>
                    </TouchableOpacity>
                </Pressable>
            </Pressable>
        </Modal>
    );
}

function SourceButton({
    icon,
    label,
    sub,
    onPress
}: {
    icon: string;
    label: string;
    sub: string;
    onPress: () => void;
}) {
    return (
        <TouchableOpacity style={styles.row} onPress={onPress} accessibilityRole="button">
            <View style={styles.iconCircle}>
                <Icon source={icon} size={22} color={colors.primary} />
            </View>
            <View style={styles.rowText}>
                <Text style={styles.rowLabel}>{label}</Text>
                <Text style={styles.rowSub}>{sub}</Text>
            </View>
        </TouchableOpacity>
    );
}

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.45)",
        justifyContent: "flex-end"
    },
    sheet: {
        backgroundColor: "#FFFFFF",
        borderTopLeftRadius: radius.xl,
        borderTopRightRadius: radius.xl,
        paddingTop: spacing.lg,
        paddingHorizontal: spacing.lg,
        paddingBottom: spacing.xl + 8,
        gap: spacing.sm
    },
    title: {
        ...typography.title,
        color: colors.textPrimary,
        marginBottom: spacing.sm
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.sm,
        borderRadius: radius.lg,
        backgroundColor: colors.backgroundSecondary
    },
    iconCircle: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: colors.primaryLight,
        alignItems: "center",
        justifyContent: "center"
    },
    rowText: {
        flex: 1
    },
    rowLabel: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    rowSub: {
        ...typography.caption,
        color: colors.textSecondary,
        marginTop: 2
    },
    cancel: {
        marginTop: spacing.sm,
        alignItems: "center",
        paddingVertical: spacing.md
    },
    cancelText: {
        ...typography.subtitle,
        color: colors.textSecondary
    }
});
